import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createCheckout, formatApiError } from "../lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, count } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty }));
      const res = await createCheckout(payload, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-forest/40 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full max-w-md bg-cream z-[70] flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-black/5">
              <h3 className="font-serif text-2xl text-forest">Your Basket ({count})</h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart" className="text-forest hover:text-terracotta" style={{ transition: "color 0.3s ease" }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-forest-soft gap-4">
                  <ShoppingBag className="w-10 h-10 opacity-40" />
                  <p className="font-serif text-xl">Your basket is quiet.</p>
                  <Link to="/shop" onClick={() => setOpen(false)} className="text-sm underline text-terracotta" data-testid="cart-empty-shop">
                    Explore the collection
                  </Link>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((it) => (
                    <li key={it.key} className="flex gap-4" data-testid={`cart-item-${it.slug}`}>
                      <img src={it.image} alt={it.name} className="w-20 h-24 object-cover rounded-xl" />
                      <div className="flex-1">
                        <p className="font-serif text-lg text-forest leading-tight">{it.name}</p>
                        {it.size && <p className="text-xs text-forest-soft uppercase tracking-wide">{it.size}</p>}
                        <p className="text-sm text-terracotta font-semibold mt-1">${it.price}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => updateQty(it.key, it.qty - 1)} data-testid={`cart-dec-${it.slug}`} className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center hover:bg-surface"><Minus className="w-3 h-3" /></button>
                          <span className="text-sm w-4 text-center">{it.qty}</span>
                          <button onClick={() => updateQty(it.key, it.qty + 1)} data-testid={`cart-inc-${it.slug}`} className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center hover:bg-surface"><Plus className="w-3 h-3" /></button>
                          <button onClick={() => removeItem(it.key)} data-testid={`cart-remove-${it.slug}`} className="ml-auto text-xs text-forest-soft hover:text-terracotta underline">Remove</button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="px-6 py-6 border-t border-black/5 bg-surface">
                <div className="flex justify-between mb-4">
                  <span className="text-forest-soft">Subtotal</span>
                  <span className="font-serif text-2xl text-forest" data-testid="cart-total">${total}</span>
                </div>
                <button
                  data-testid="cart-checkout"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full rounded-full bg-forest hover:bg-terracotta text-cream py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-60"
                  style={{ transition: "background-color 0.3s ease" }}
                >
                  {loading ? "Redirecting…" : "Proceed to Checkout"}
                </button>
                <p className="text-center text-xs text-forest-soft mt-3">Secure checkout · Free local pickup in Racine, WI</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
